import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, MapPin, Truck, Clock } from "lucide-react";
import { suburbPages, restaurantInfo } from "../data/mock";

export default function SuburbPage() {
  const { slug } = useParams();
  const s = suburbPages.find((x) => x.slug === slug);
  const title = s ? `Liberian food delivery in ${s.suburb} — Mamamina's Soulfeed` : "Delivery — Mamamina's Soulfeed";
  React.useEffect(() => {
    const prev = document.title;
    document.title = title;
    return () => { document.title = prev; };
  }, [title]);
  if (!s) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[var(--mm-bg)] grain">
      <div className="max-w-[1100px] mx-auto px-6 md:px-12 py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-xs tracking-[0.24em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors mb-10">
          <ArrowLeft size={14} /> Home
        </Link>

        <div className="section-label mb-3">Delivery to {s.postcode}</div>
        <h1 className="font-display italic text-5xl md:text-7xl text-[var(--mm-cream)] leading-tight mb-6">
          Liberian food<br />delivered to <span className="text-[var(--mm-accent)]">{s.suburb}.</span>
        </h1>
        <p className="text-[var(--mm-cream-muted)] font-light text-lg leading-relaxed max-w-2xl mb-12">
          {s.intro}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <Fact icon={Truck} label="Delivery fee" value={`$${s.fee}`} sub={s.zone} />
          <Fact icon={Clock} label="Typical arrival" value={"25 – 40 min"} sub="peak may vary" />
          <Fact icon={MapPin} label="Coverage" value={s.range} sub="from our kitchen" />
        </div>

        <div className="p-8 md:p-10 bg-[var(--mm-bg-alt)] border border-[var(--mm-line)] mb-12">
          <div className="section-label mb-4">What to order</div>
          <ul className="space-y-3 text-[var(--mm-cream)]">
            <li className="flex items-baseline gap-3">
              <span className="text-[var(--mm-accent)]">01</span>
              <span className="font-display italic text-lg">Attieké Feast Platter</span>
              <span className="text-[var(--mm-cream-muted)] text-sm">— whole grilled fish, plantain, eggs, salad, sauces</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="text-[var(--mm-accent)]">02</span>
              <span className="font-display italic text-lg">Pepper Soup & Fufu</span>
              <span className="text-[var(--mm-cream-muted)] text-sm">— bright, spicy, aromatic broth</span>
            </li>
            <li className="flex items-baseline gap-3">
              <span className="text-[var(--mm-accent)]">03</span>
              <span className="font-display italic text-lg">Dry Rice</span>
              <span className="text-[var(--mm-cream-muted)] text-sm">— fried fish + chicken + plantains + egg + our red palm oil</span>
            </li>
          </ul>
        </div>

        <div className="text-center">
          <Link to="/#menu" className="btn-primary">Browse Full Menu</Link>
          <a href={`tel:${restaurantInfo.contact.phone}`} className="ml-3 btn-outline inline-block">
            Call {restaurantInfo.contact.phoneDisplay}
          </a>
        </div>
      </div>
    </div>
  );
}

function Fact({ icon: Icon, label, value, sub }) {
  return (
    <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)]">
      <div className="flex items-center gap-3 mb-3">
        <Icon size={14} className="text-[var(--mm-accent)]" />
        <span className="section-label">{label}</span>
      </div>
      <div className="font-display text-3xl text-[var(--mm-cream)]">{value}</div>
      {sub && <div className="text-xs text-[var(--mm-cream-muted)] mt-1">{sub}</div>}
    </div>
  );
}
